import React, { ChangeEvent, useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { changeSuperAdminPasswordAPI } from '../../api/superAdminAuth';

const T = {
    navy: '#1A305D',
    blue: '#3D73DD',
    text2: '#5E7093',
    text3: '#8896B0',
    border: '#E2E8F0',
    card: '#FFFFFF',
    error: '#FF3E4A',
    errorBg: '#FFF0F0',
    success: '#00A793',
    successBg: '#E5F8F3',
    inputBg: '#F8FAFC',
};

type Props = {
    onClose: () => void;
    authorization: string;
    superAdminId: string;
};

const ModalSuperAdminPasswordChange = ({ onClose, authorization, superAdminId }: Props) => {
    const [newPassword, setNewPassword] = useState('');
    const [newPasswordConfirm, setNewPasswordConfirm] = useState('');
    const [errorMsg, setErrorMsg] = useState('');
    const [success, setSuccess] = useState(false);

    const isDisabled = newPassword.length < 8 || newPasswordConfirm.length < 1;

    const changePassword = useMutation({
        mutationFn: async () => await changeSuperAdminPasswordAPI(superAdminId, newPassword, authorization),
        onSuccess: (res) => {
            if (res.status === 200) {
                setErrorMsg('');
                setSuccess(true);
            } else if (res.status === 400) {
                setErrorMsg('비밀번호 형식이 올바르지 않습니다. 영문, 숫자, 특수문자를 포함한 8~20자로 입력해주세요.');
            } else if (res.status === 401) {
                setErrorMsg('인증이 만료되었습니다. 다시 로그인해주세요.');
            } else if (res.status === 406) {
                setErrorMsg('접근 권한이 없습니다.');
            } else {
                setErrorMsg('비밀번호 변경에 실패했습니다. 다시 시도해주세요.');
            }
        },
    });

    const onClickConfirm = () => {
        setErrorMsg('');
        if (newPassword !== newPasswordConfirm) {
            setErrorMsg('비밀번호가 일치하지 않습니다.');
            return;
        }
        changePassword.mutate();
    };

    return (
        <div style={{
            position: 'fixed', top: 0, left: 0, width: '100%', height: '100%',
            background: 'rgba(0,0,0,0.4)', display: 'flex', justifyContent: 'center',
            alignItems: 'center', zIndex: 3000,
        }}>
            <div style={{
                background: T.card, borderRadius: 12, width: 480, padding: '32px 36px',
                boxShadow: '0 8px 32px rgba(26,48,93,0.15)',
            }}>
                {/* 헤더 */}
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 28 }}>
                    <span style={{ fontSize: 18, fontWeight: 700, color: T.navy }}>비밀번호 변경</span>
                    <button
                        onClick={onClose}
                        style={{ background: 'none', border: 'none', cursor: 'pointer', color: T.text3, padding: 4, lineHeight: 1 }}
                    >
                        <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                            <path d="M15 5L5 15M5 5l10 10" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"/>
                        </svg>
                    </button>
                </div>

                {success ? (
                    /* 성공 상태 */
                    <div style={{ textAlign: 'center', padding: '20px 0 28px' }}>
                        <div style={{
                            width: 56, height: 56, borderRadius: '50%', background: T.successBg,
                            display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 16px',
                        }}>
                            <svg width="28" height="28" viewBox="0 0 28 28" fill="none">
                                <path d="M6 14l6 6 10-12" stroke={T.success} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/>
                            </svg>
                        </div>
                        <p style={{ fontSize: 16, fontWeight: 700, color: T.navy, marginBottom: 8 }}>비밀번호가 변경되었습니다</p>
                        <p style={{ fontSize: 13, color: T.text3, marginBottom: 28 }}>새로운 비밀번호로 변경이 완료되었습니다.</p>
                        <button
                            onClick={onClose}
                            style={{
                                width: '100%', height: 46, borderRadius: 8, border: 'none',
                                background: T.blue, color: '#fff', fontSize: 14, fontWeight: 600, cursor: 'pointer',
                            }}
                        >
                            확인
                        </button>
                    </div>
                ) : (
                    <>
                        {/* 새 비밀번호 */}
                        <div style={{ marginBottom: 20 }}>
                            <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: T.text2, marginBottom: 8 }}>
                                새 비밀번호
                            </label>
                            <input
                                type="password"
                                maxLength={20}
                                value={newPassword}
                                onChange={(e: ChangeEvent<HTMLInputElement>) => { setNewPassword(e.target.value.trim()); setErrorMsg(''); }}
                                placeholder="새 비밀번호를 입력해주세요"
                                style={{
                                    width: '100%', height: 46, borderRadius: 8, border: `1px solid ${T.border}`,
                                    background: T.inputBg, fontSize: 14, padding: '0 14px',
                                    outline: 'none', boxSizing: 'border-box', color: T.navy,
                                }}
                            />
                        </div>

                        {/* 새 비밀번호 확인 */}
                        <div style={{ marginBottom: 10 }}>
                            <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: T.text2, marginBottom: 8 }}>
                                새 비밀번호 확인
                            </label>
                            <input
                                type="password"
                                maxLength={20}
                                value={newPasswordConfirm}
                                onChange={(e: ChangeEvent<HTMLInputElement>) => { setNewPasswordConfirm(e.target.value.trim()); setErrorMsg(''); }}
                                placeholder="새 비밀번호를 다시 입력해주세요"
                                style={{
                                    width: '100%', height: 46, borderRadius: 8,
                                    border: `1px solid ${errorMsg ? T.error : T.border}`,
                                    background: T.inputBg, fontSize: 14, padding: '0 14px',
                                    outline: 'none', boxSizing: 'border-box', color: T.navy,
                                }}
                            />
                        </div>

                        {/* 힌트 / 에러 메시지 */}
                        {errorMsg ? (
                            <p style={{ fontSize: 12, color: T.error, marginBottom: 24, minHeight: 18 }}>{errorMsg}</p>
                        ) : (
                            <p style={{ fontSize: 12, color: T.text3, marginBottom: 24, minHeight: 18 }}>
                                영문, 숫자, 특수문자를 포함한 8~20자로 작성해주세요.
                            </p>
                        )}

                        {/* 버튼 */}
                        <div style={{ display: 'flex', gap: 10 }}>
                            <button
                                onClick={onClose}
                                style={{
                                    flex: 1, height: 46, borderRadius: 8, border: `1px solid ${T.border}`,
                                    background: T.card, color: T.text2, fontSize: 14, fontWeight: 600, cursor: 'pointer',
                                }}
                            >
                                취소
                            </button>
                            <button
                                onClick={onClickConfirm}
                                disabled={isDisabled || changePassword.isLoading}
                                style={{
                                    flex: 1, height: 46, borderRadius: 8, border: 'none',
                                    background: isDisabled ? T.border : T.blue,
                                    color: isDisabled ? T.text3 : '#fff',
                                    fontSize: 14, fontWeight: 600,
                                    cursor: isDisabled ? 'default' : 'pointer',
                                    transition: 'background 0.2s',
                                }}
                            >
                                {changePassword.isLoading ? '변경 중...' : '변경하기'}
                            </button>
                        </div>
                    </>
                )}
            </div>
        </div>
    );
};

export default ModalSuperAdminPasswordChange;
